/**
 * The field-level detail an `INVALID_ARGUMENT` carries — the **one** declaration of it.
 *
 * `contracts/operations.md` § Common envelope specifies `INVALID_ARGUMENT` "(field-level details)"
 * and `BOUNDARIES.md` § 6 says it "carries field-level details from Zod". That detail crosses the
 * wire, so it is part of the operation contract and belongs here, beside the error codes and the
 * message keys — not declared once in `functions/src/lib/dispatch.ts` and again in
 * `apps/web/src/lib/errors.ts`, where the two could drift apart while both test suites stayed green.
 *
 * Both sides import this module: the dispatcher builds issues with `fieldIssuesFromZodError` and the
 * client rebuilds them with `parseFieldIssues`, so the wire shape has a single definition and a
 * single set of tests. `BOUNDARIES.md` § 2 holds: `zod` is still the only runtime dependency here,
 * and nothing in this module knows about firebase, react or I/O.
 */
import { z } from 'zod';

/**
 * The exact keys of a `FieldIssue`, in wire order. Exported so a test on either side can assert the
 * shape against the contract rather than against its own copy of it.
 */
export const FIELD_ISSUE_KEYS = Object.freeze(['path', 'code', 'message'] as const);

export type FieldIssueKey = (typeof FIELD_ISSUE_KEYS)[number];

export interface FieldIssue {
  /** Dotted path inside the envelope, e.g. `params.marker`; `op` for the discriminator itself. */
  readonly path: string;
  /** Zod's stable issue code (`invalid_type`, `unrecognized_keys`, …) — never a localized string. */
  readonly code: string;
  /**
   * Developer-facing English text. The user-facing Georgian string comes from the operation's
   * message key, so a client may use `path` and `code` to mark a field but must never render this
   * (`BOUNDARIES.md` § 6: the client never composes an error message from a server string).
   */
  readonly message: string;
}

/**
 * The path used when a Zod issue has no path at all — the value fails as a whole.
 * Only produced when no prefix is supplied, which is the server's case.
 */
export const ROOT_ISSUE_PATH = '(root)';

/**
 * Unknown keys are stripped rather than rejected: an issue that arrives with an extra property is
 * still a usable issue, and rebuilding it from exactly these three fields is what stops anything
 * else reaching a component's props.
 */
export const fieldIssueSchema = z.object({
  path: z.string(),
  code: z.string(),
  message: z.string(),
});

/** The canonical empty list. Frozen and shared, so an error with no detail allocates nothing. */
export const NO_FIELD_ISSUES: readonly FieldIssue[] = Object.freeze([]);

/** Build one frozen issue. The only constructor either side uses. */
export function createFieldIssue(path: string, code: string, message: string): FieldIssue {
  return Object.freeze({ path, code, message });
}

/**
 * Deep-copy and freeze a list of issues.
 *
 * Both the array **and** every entry are rebuilt, so a caller that keeps a mutable reference to what
 * it passed in cannot change what the error carries afterwards. `Object.freeze` on the array alone
 * would leave each entry mutable, which is the defect this exists to prevent.
 */
export function freezeFieldIssues(issues: readonly FieldIssue[]): readonly FieldIssue[] {
  if (issues.length === 0) return NO_FIELD_ISSUES;
  return Object.freeze(issues.map((issue) => createFieldIssue(issue.path, issue.code, issue.message)));
}

/**
 * Read an `issues` payload off the wire, keeping only well-formed entries.
 *
 * Total by construction: an absent, `null`, non-array or wrongly shaped payload yields the empty
 * list rather than throwing, because a malformed diagnostic must never turn an error the caller
 * could act on into a crash. Malformed entries inside a valid array are dropped and the good ones
 * kept.
 */
export function parseFieldIssues(value: unknown): readonly FieldIssue[] {
  if (!Array.isArray(value)) return NO_FIELD_ISSUES;
  const issues: FieldIssue[] = [];
  for (const entry of value) {
    const parsed = fieldIssueSchema.safeParse(entry);
    if (parsed.success) {
      issues.push(createFieldIssue(parsed.data.path, parsed.data.code, parsed.data.message));
    }
  }
  return issues.length === 0 ? NO_FIELD_ISSUES : Object.freeze(issues);
}

/**
 * Zod issues → the wire shape.
 *
 * `prefix` is what lets one function serve both sides. The dispatcher validates the whole envelope,
 * so its paths are already envelope-relative and it passes no prefix; the client validates the
 * params alone and passes `"params"`, so a request it rejects before anything leaves the browser
 * reports `params.marker` exactly as the server would have.
 *
 * The argument is typed `unknown` and read structurally, so a caught value that is not a `ZodError`
 * — a plain `Error`, a string, `undefined` — yields the empty list instead of throwing.
 */
export function fieldIssuesFromZodError(error: unknown, prefix?: string): readonly FieldIssue[] {
  const raw = (error as { issues?: unknown } | null | undefined)?.issues;
  if (!Array.isArray(raw)) return NO_FIELD_ISSUES;

  const issues: FieldIssue[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'object' || entry === null) continue;
    const { path, code, message } = entry as { path?: unknown; code?: unknown; message?: unknown };
    if (typeof code !== 'string' || typeof message !== 'string') continue;
    const segments = Array.isArray(path) ? path.map(String) : [];
    const dotted =
      prefix === undefined
        ? segments.length > 0
          ? segments.join('.')
          : ROOT_ISSUE_PATH
        : [prefix, ...segments].join('.');
    issues.push(createFieldIssue(dotted, code, message));
  }
  return issues.length === 0 ? NO_FIELD_ISSUES : Object.freeze(issues);
}
